import { normalizeKhmerText } from './khmer-normalizer'

interface DictEntry {
  '': string        // word
  p?: string[]      // part of speech
  d?: string[]      // definitions
  f?: string[]      // pronunciation/forms
  s?: string[]      // synonyms
}

interface LookupResult {
  word: string
  pos: string[]
  definitions: string[]
  pronunciation: string
  forms: string[]
  source: 'khmer' | 'english'
  error?: string
}

type Listener = () => void

let isLoaded = false
let isLoading = false
let khToEng: Map<string, DictEntry> = new Map()
let engChunks: Map<string, Map<string, string>> = new Map()
let engChunkLoading: Map<string, boolean> = new Map()
let khToKh: Map<string, { word: string; pos: string; pron: string; definitions: string[]; examples: string[] }> = new Map()
let khDictLoaded = false
let khDictLoading = false
const listeners = new Set<Listener>()

function notify() {
  listeners.forEach(fn => fn())
}

export const DictionaryService = {
  get isLoaded() { return isLoaded },
  get isLoading() { return isLoading },
  get khDictLoaded() { return khDictLoaded },

  subscribe(fn: Listener) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },

  async load(): Promise<void> {
    if (isLoaded || isLoading) return
    isLoading = true
    notify()
    try {
      const res = await fetch('/dictionary-km.json')
      const text = await res.text()
      const lines = text.trim().split('\n')
      for (const line of lines) {
        try {
          const entry: DictEntry = JSON.parse(line)
          const word = normalizeKhmerText(entry[''])
          if (!word) continue
          khToEng.set(word, entry)
        } catch {}
      }
      isLoaded = true
    } catch (err) {
      console.error('Failed to load dictionary:', err)
    }
    isLoading = false
    notify()
  },

  lookupKhmer(word: string): LookupResult | null {
    const normalized = normalizeKhmerText(word)
    const entry = khToEng.get(normalized)
    if (!entry) return null
    return {
      word,
      pos: entry.p || [],
      definitions: entry.d || [],
      pronunciation: (entry.f || []).find(f => /^[a-zɑɒɔɛɨəŋɲ]/.test(f)) || '',
      forms: (entry.f || []).filter(f => !/^[a-zɑɒɔɛɨəŋɲ]/.test(f)).slice(0, 5),
      source: 'khmer',
    }
  },

  async loadEngKhChunk(letter: string): Promise<void> {
    if (engChunks.has(letter) || engChunkLoading.get(letter)) return
    engChunkLoading.set(letter, true)
    try {
      const res = await fetch(`/eng-kh-chunks/${letter}.json`)
      const data = await res.json()
      const map = new Map<string, string>()
      for (const [key, entry] of Object.entries(data) as [string, { k: string; e?: string }][]) {
        map.set(key, entry.k)
      }
      engChunks.set(letter, map)
    } catch (err) {
      console.error(`Failed to load English dictionary chunk '${letter}':`, err)
    }
    engChunkLoading.set(letter, false)
  },

  async lookupEnglish(word: string): Promise<LookupResult | null> {
    const lower = word.toLowerCase().trim()
    const first = lower.charAt(0)
    let chunk = engChunks.get(first)
    if (!chunk && !engChunkLoading.get(first)) {
      await this.loadEngKhChunk(first)
      chunk = engChunks.get(first)
    }
    if (chunk) {
      const definition = chunk.get(lower)
      if (definition) {
        return {
          word,
          pos: [],
          definitions: [definition],
          pronunciation: '',
          forms: [],
          source: 'english',
        }
      }
    }
    return null
  },

  async lookupEnglishApi(word: string): Promise<LookupResult | null> {
    try {
      const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`)
      if (!res.ok) return null
      const data = await res.json()
      if (!Array.isArray(data) || !data[0]) return null
      const entry = data[0]
      const meanings = entry.meanings || []
      const defs: string[] = []
      for (const m of meanings) {
        for (const d of m.definitions || []) {
          defs.push(d.definition)
        }
      }
      return {
        word: entry.word,
        pos: [...new Set<string>(meanings.map((m: any) => m.partOfSpeech))],
        definitions: defs,
        pronunciation: entry.phonetic || (entry.phonetics?.[0]?.text) || '',
        forms: [],
        source: 'english',
      }
    } catch {
      return null
    }
  },

  async loadKhmerDict(): Promise<void> {
    if (khDictLoaded || khDictLoading) return
    khDictLoading = true
    notify()
    try {
      const res = await fetch('/khmer_dict.json')
      const data: any[] = await res.json()
      for (const entry of data) {
        const word = normalizeKhmerText(entry.t_main || '')
        const subword = normalizeKhmerText(entry.t_subword || '')
        const key = normalizeKhmerText(subword || word)
        if (!key) continue

        const pos = entry.t_pos || ''
        const pron = entry.t_pron || ''
        const exp = entry.t_exp || ''
        const exam = entry.t_exam || ''

        const existing = khToKh.get(key)
        if (existing) {
          existing.definitions.push(exp)
          if (exam) existing.examples.push(exam)
          if (pos && !existing.pos.includes(pos)) existing.pos += ',' + pos
        } else {
          khToKh.set(key, {
            word: key,
            pos,
            pron,
            definitions: [exp],
            examples: exam ? [exam] : [],
          })
        }
      }
      khDictLoaded = true
    } catch (err) {
      console.error('Failed to load Khmer dictionary:', err)
    }
    khDictLoading = false
    notify()
  },

  lookupKhmerKhmer(word: string): LookupResult | null {
    const normalized = normalizeKhmerText(word)
    const entry = khToKh.get(normalized)
    if (!entry) return null
    return {
      word: entry.word,
      pos: entry.pos ? entry.pos.split(',').filter(Boolean) : [],
      definitions: entry.definitions,
      pronunciation: entry.pron,
      forms: entry.examples,
      source: 'khmer',
    }
  },
}

export function segmentKhmerWord(paragraph: string, cursorPos: number): string | null {
  if (!isLoaded || cursorPos < 0 || cursorPos >= paragraph.length) return null
  const normalized = normalizeKhmerText(paragraph)
  let best = ''
  let left = cursorPos
  let right = cursorPos + 1
  for (let len = 1; len <= 20; len++) {
    for (let l = Math.max(0, cursorPos - len + 1); l <= cursorPos; l++) {
      const r = l + len
      if (r > normalized.length) break
      const candidate = normalized.slice(l, r)
      if (khToEng.has(candidate) && candidate.length > best.length) {
        best = candidate
      }
    }
  }
  return best || null
}
